<script setup>
/**
 * ScanMock — the hostess check-in flow, redrawn as a device mockup.
 * Faithful to the real app: QR scan → identity confirmed → attendance
 * written with the welcome-desk instance. Decorative (aria-hidden on
 * the wrapper); the informative alt lives on the parent section.
 */
</script>

<template>
  <div class="relative mx-auto w-[290px]" aria-hidden="true">
    <!-- phone body -->
    <div class="rounded-[2.4rem] border-[10px] border-slate-900 bg-slate-900 shadow-[0_32px_70px_-28px_rgba(15,23,42,0.55)] overflow-hidden">
      <div class="bg-white">
        <!-- status / app bar -->
        <div class="bg-[#5b3f9e] text-white px-4 pt-3 pb-2.5">
          <div class="flex items-center justify-between text-[10px] font-mono opacity-70 mb-1.5" dir="ltr">
            <span>09:42</span>
            <span>●●●</span>
          </div>
          <div class="flex items-center justify-between">
            <div>
              <div class="text-[13px] font-semibold leading-tight">Bifrost — Accueil</div>
              <div class="text-[10px] opacity-75">Poste : Hall A · Entrée principale</div>
            </div>
            <span class="inline-flex items-center gap-1 text-[10px] bg-white/15 rounded-full px-2 py-0.5">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> En ligne
            </span>
          </div>
        </div>

        <!-- camera viewport -->
        <div class="relative bg-slate-950 aspect-[4/4.4] overflow-hidden">
          <!-- fake blurred hall -->
          <div class="absolute inset-0 opacity-40" style="background: radial-gradient(80% 60% at 50% 30%, #334155 0%, #0f172a 70%)"></div>
          <!-- QR being scanned -->
          <svg viewBox="0 0 84 84" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 opacity-90">
            <rect x="0" y="0" width="84" height="84" rx="6" fill="#fff" />
            <g fill="#0f172a">
              <rect x="8" y="8" width="20" height="20" rx="2" /><rect x="12" y="12" width="12" height="12" fill="#fff" rx="1" /><rect x="15" y="15" width="6" height="6" />
              <rect x="56" y="8" width="20" height="20" rx="2" /><rect x="60" y="12" width="12" height="12" fill="#fff" rx="1" /><rect x="63" y="15" width="6" height="6" />
              <rect x="8" y="56" width="20" height="20" rx="2" /><rect x="12" y="60" width="12" height="12" fill="#fff" rx="1" /><rect x="15" y="63" width="6" height="6" />
              <rect x="36" y="10" width="5" height="5" /><rect x="44" y="16" width="5" height="5" /><rect x="36" y="24" width="5" height="5" />
              <rect x="12" y="36" width="5" height="5" /><rect x="22" y="42" width="5" height="5" /><rect x="32" y="38" width="5" height="5" />
              <rect x="42" y="44" width="5" height="5" /><rect x="52" y="38" width="5" height="5" /><rect x="62" y="44" width="5" height="5" />
              <rect x="70" y="36" width="5" height="5" /><rect x="38" y="56" width="5" height="5" /><rect x="46" y="64" width="5" height="5" />
              <rect x="56" y="58" width="5" height="5" /><rect x="66" y="66" width="5" height="5" /><rect x="38" y="70" width="5" height="5" />
            </g>
          </svg>
          <!-- scan brackets -->
          <svg viewBox="0 0 200 200" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 text-emerald-400">
            <g stroke="currentColor" stroke-width="4" fill="none" stroke-linecap="round">
              <path d="M10 46 V18 a8 8 0 0 1 8-8 h28" />
              <path d="M154 10 h28 a8 8 0 0 1 8 8 v28" />
              <path d="M190 154 v28 a8 8 0 0 1 -8 8 h-28" />
              <path d="M46 190 H18 a8 8 0 0 1 -8-8 v-28" />
            </g>
          </svg>
          <!-- success toast -->
          <div class="absolute inset-x-3 bottom-3 rounded-xl bg-white/97 backdrop-blur px-3.5 py-3 shadow-lg">
            <div class="flex items-center gap-3">
              <span class="flex w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center shrink-0">
                <svg viewBox="0 0 20 20" fill="none" class="w-4.5 h-4.5"><path d="M4 10.5 8 14.5 16 6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </span>
              <div class="min-w-0">
                <div class="text-[13px] font-semibold text-slate-900 truncate">Amina Benali</div>
                <div class="text-[11px] text-slate-500 truncate">VIP · Laboratoire Alger · 09:42:17</div>
              </div>
            </div>
          </div>
        </div>

        <!-- live counter -->
        <div class="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-100">
          <div class="text-[11px] text-slate-500">Présents sur site</div>
          <div class="flex items-baseline gap-1.5">
            <span class="text-lg font-semibold text-slate-900 tabular-nums" dir="ltr">312</span>
            <span class="text-[10px] text-emerald-600 font-medium" dir="ltr">+9 / 10 min</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
